import React from 'react';
import { DragIcon, SettingsIcon } from 'assests/GmsIcons';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import { TSetPriorityMode } from '../Types';
import {
    SET_MODES_PRIORITY_OBJECT,
    MODES_WITH_SETTINGS,
    TITLE_LABEL,
    MIN_PRODUCT_LABEL
} from '../ARSConstants';
import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import TextField from 'Components/DesignSystem/TextField/TextField';

type ModeItemRowVariant = 'combined' | 'individual';

type ModeItemRowProps = {
    mode: TSetPriorityMode;
    index: number;
    variant: ModeItemRowVariant;
    onToggle: (checked: boolean) => void;
    onSettingsClick: () => void;
    onTitleChange?: (value: string) => void;
    onMinProductChange?: (value: number) => void;
    disabled?: boolean;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
    id?: string;
};

export const ModeItemRow = ({
    mode,
    index,
    variant,
    onToggle,
    onSettingsClick,
    onTitleChange,
    onMinProductChange,
    disabled = false,
    provided,
    snapshot,
    id = 'mode-item-row'
}: ModeItemRowProps) => {
    const hasSettings = MODES_WITH_SETTINGS.includes(
        mode.mode as typeof MODES_WITH_SETTINGS[number]
    );

    const modeDisplayInfo = SET_MODES_PRIORITY_OBJECT[mode.mode] ?? {
        title: mode.mode,
        description: mode.description || ''
    };

    const handleTitleChange = (e: string) => {
        onTitleChange?.(e);
    };

    const handleMinProductChange = (e: string) => {
        const value = parseInt(e, 10);
        if (!isNaN(value) && value >= 1 && value <= 100) {
            onMinProductChange?.(value);
        } else if (e === '') {
            onMinProductChange?.(1);
        }
    };

    const handleMinProductKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === 'e' || e.key === '+' || e.key === '.') {
            e.preventDefault();
        }
    };

    const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onToggle(e.target.checked);
    };

    const handleSettingsClick = () => {
        if (hasSettings && !disabled) {
            onSettingsClick();
        }
    };

    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`
                tw-min-h-[56px]
                tw-w-full
                tw-px-3 tw-py-2
                tw-bg-white
                tw-rounded
                tw-border
                tw-border-[#ebecf0]
                tw-flex
                tw-items-center
                tw-gap-3
                ${snapshot.isDragging ? 'tw-opacity-70 tw-shadow-lg' : ''}
            `}
            data-testid={`${id}-${mode.mode}`}
        >
            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                <DragIcon />
                <div className="tw-text-[#6d788c] tw-text-xs tw-uppercase tw-leading-[14px] tw-w-4 tw-text-center">
                    {index + 1}
                </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-text-[#172b4d] tw-text-sm tw-font-semibold">
                    {modeDisplayInfo.title}
                </div>
                <div className="tw-w-[15px] tw-h-[15px]">
                    <OverlayIcon
                        message={
                            mode.description
                                ? mode.description
                                : modeDisplayInfo.description
                        }
                    />
                </div>
            </div>

            {variant === 'individual' && (
                <div className="tw-flex tw-items-center tw-gap-4 tw-ml-auto">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <label
                            htmlFor={`${id}-${mode.mode}-title`}
                            className="tw-text-[#6d788c] tw-text-sm tw-leading-tight"
                        >
                            {TITLE_LABEL}
                        </label>
                        <TextField
                            label=''
                            type="text"
                            value={mode.title}
                            onChange={(e) => handleTitleChange(e)}
                            disabled={disabled}
                            data-testid={`${id}-${mode.mode}-title-input`}
                            className='tw-w-[250px]'
                        />
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-5">
                        <label
                            htmlFor={`${id}-${mode.mode}-min-product`}
                            className="tw-text-[#6d788c] tw-text-sm tw-leading-tight"
                        >
                            {MIN_PRODUCT_LABEL}
                        </label>
                        <TextField
                            label=''
                            type="number"
                            minValue={1}
                            maxValue={100}
                            value={String(mode.minNumProducts ?? 1)}
                            onChange={handleMinProductChange}
                            disabled={disabled}
                            data-testid={`${id}-${mode.mode}-min-product-input`}
                        />
                    </div>

                    <ToggleButton
                        name={`${id}-${mode.mode}-toggle`}
                        value={mode.active}
                        onChange={handleToggleChange}
                        disabled={disabled}
                    />
                    <button
                        type="button"
                        onClick={handleSettingsClick}
                        disabled={disabled || !hasSettings}
                        className={`
                            tw-p-1
                            ${hasSettings && !disabled
                                ? 'tw-cursor-pointer tw-opacity-100'
                                : 'tw-cursor-not-allowed tw-opacity-30'
                            }
                        `}
                        data-testid={`${id}-${mode.mode}-settings-btn`}
                        aria-label={`Settings for ${modeDisplayInfo.title}`}
                    >
                        <SettingsIcon />
                    </button>
                </div>
            )}

            {variant === 'combined' && (
                <div className="tw-flex tw-items-center tw-gap-9 tw-pe-32 tw-ml-auto">
                    <ToggleButton
                        name={`${id}-${mode.mode}-toggle`}
                        value={mode.active}
                        onChange={handleToggleChange}
                        disabled={disabled}
                    />
                    <button
                        type="button"
                        onClick={handleSettingsClick}
                        disabled={disabled || !hasSettings}
                        className={`
                            tw-p-1
                            ${hasSettings && !disabled
                                ? 'tw-cursor-pointer tw-opacity-100'
                                : 'tw-cursor-not-allowed tw-opacity-30'
                            }
                        `}
                        data-testid={`${id}-${mode.mode}-settings-btn`}
                        aria-label={`Settings for ${modeDisplayInfo.title}`}
                    >
                        <SettingsIcon />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ModeItemRow;